import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "./Template.css";
import { publicApiClient } from "../../../../../api/apiClient";

const normalizeDate = (value) => {
  if (!value) return "";

  if (typeof value === "string") {
    return value.slice(0, 10);
  }

  return "";
};

const normalizeCategory = (value) => {
  if (!value) return "자료";

  const categoryMap = {
    ALL: "전체",
    BUDGET: "회계",
    EVENT: "행사/사업",
    ADMINISTRATION: "행정/운영",
    RULE: "회칙",
    BYLAW: "세칙",
    FORM: "양식",
    DATA: "자료",
    MINUTES: "회의록",
    MEETING: "회의록",
    ETC: "자료",
  };

  return categoryMap[value] || value;
};

const getDataFromApiResponse = (result) => {
  return result?.data?.data || result?.data || result?.result || result;
};

const normalizeFileItem = (file) => {
  if (!file || typeof file !== "object") return null;

  return {
    id:
      file.formFileId ||
      file.collegeFormFileId ||
      file.collegeFormsFileId ||
      file.fileId ||
      file.id ||
      null,
    name:
      file.name ||
      file.originalName ||
      file.originalFileName ||
      file.fileName ||
      file.savedName ||
      "첨부파일",
    size: file.size || file.fileSize || "",
    url: file.url || file.fileUrl || file.downloadUrl || file.filePath || "",
  };
};

const normalizeFiles = (item) => {
  if (!item) return [];

  const fileList = [];

  const possibleArrays = [
    item.files,
    item.fileList,
    item.formFiles,
    item.formFileList,
    item.collegeFormFiles,
    item.collegeFormsFiles,
    item.fileResponses,
    item.formFileResponses,
    item.attachments,
  ];

  possibleArrays.forEach((array) => {
    if (Array.isArray(array)) {
      fileList.push(...array);
    }
  });

  const possibleSingleFiles = [
    item.file,
    item.attachment,
    item.attachFile,
    item.formFile,
    item.fileResponse,
    item.formFileResponse,
  ];

  possibleSingleFiles.forEach((fileItem) => {
    if (fileItem && typeof fileItem === "object") {
      fileList.push(fileItem);
    }
  });

  const normalizedFiles = fileList.map(normalizeFileItem).filter(Boolean);

  return normalizedFiles.filter((file, index, array) => {
    const key = file.id || file.url || file.name;

    return (
      array.findIndex((target) => {
        const targetKey = target.id || target.url || target.name;
        return targetKey === key;
      }) === index
    );
  });
};

const normalizePost = (item) => {
  if (!item) return null;

  const files = normalizeFiles(item);
  const rawDate = item.createdAt || item.createdDate || item.date || item.updatedAt || "";

  return {
    id:
      item.collegeFormsId ||
      item.collegeFormId ||
      item.formId ||
      item.id ||
      item.postId,
    category: normalizeCategory(
      item.category || item.listCategory || item.formListCategory
    ),
    title: item.title || "",
    writer: item.name || "관리자",
    date: normalizeDate(rawDate),
    rawDate,
    view: item.viewer ?? item.view ?? item.viewCount ?? item.views ?? 0,
    content: item.content || item.body || "",
    files,
    file: files[0] || null,
  };
};

const sortLatestFirst = (a, b) => {
  const dateA = new Date(a.rawDate || a.date).getTime();
  const dateB = new Date(b.rawDate || b.date).getTime();

  if (!Number.isNaN(dateA) && !Number.isNaN(dateB)) {
    return dateB - dateA;
  }

  return Number(b.id || 0) - Number(a.id || 0);
};

export default function TemplateDetail({
  unit: unitProp,
  collegeId,
  posts = [],
}) {
  const { unit: routeUnit, id } = useParams();
  const navigate = useNavigate();

  const unit = unitProp || routeUnit;

  const [detailItem, setDetailItem] = useState(null);
  const [isDetailLoading, setIsDetailLoading] = useState(false);

  const navigationPosts = useMemo(() => {
    return [...posts].sort(sortLatestFirst);
  }, [posts]);

  const currentIndex = navigationPosts.findIndex(
    (post) => String(post.id) === String(id)
  );

  const listItem = currentIndex >= 0 ? navigationPosts[currentIndex] : null;

  useEffect(() => {
    const fetchDetail = async () => {
      if (!collegeId || !id) {
        setDetailItem(null);
        return;
      }

      try {
        setIsDetailLoading(true);

        const result = await publicApiClient(
          `/api/colleges/${collegeId}/form/${id}`
        );

        const data = getDataFromApiResponse(result);
        setDetailItem(normalizePost(data));
      } catch (error) {
        console.error("중운위 게시글 상세 조회 실패:", error);
        setDetailItem(null);
      } finally {
        setIsDetailLoading(false);
      }
    };

    fetchDetail();
  }, [collegeId, id]);

  const item = detailItem || listItem;

  const writerName = item?.writer || "관리자";
  const content = item?.content || "";
  const files = item?.files || (item?.file ? [item.file] : []);

  const nextItem = useMemo(() => {
    if (currentIndex > 0) {
      return {
        id: navigationPosts[currentIndex - 1].id,
        title: navigationPosts[currentIndex - 1].title,
      };
    }

    return null;
  }, [currentIndex, navigationPosts]);

  const prevItem = useMemo(() => {
    if (currentIndex >= 0 && currentIndex < navigationPosts.length - 1) {
      return {
        id: navigationPosts[currentIndex + 1].id,
        title: navigationPosts[currentIndex + 1].title,
      };
    }

    return null;
  }, [currentIndex, navigationPosts]);

  if (isDetailLoading) {
    return (
      <div className="cc-unit-detail-not-found">
        게시글을 불러오는 중입니다.
      </div>
    );
  }

  if (!item) {
    return (
      <div className="cc-unit-detail-not-found">
        게시글을 찾을 수 없습니다.
      </div>
    );
  }

  return (
    <div className="cc-unit-detail-wrap">
      <div className="cc-unit-detail-inner">
        <div className="cc-unit-detail-title">{item.title}</div>

        <div className="cc-unit-detail-meta-author">
          <span className="cc-unit-detail-author-name">{writerName}</span>
        </div>

        <div className="cc-unit-detail-meta-row">
          <span>
            <span className="cc-unit-detail-label">작성일</span> {item.date}
          </span>

          <span>
            <span className="cc-unit-detail-label">조회수</span> {item.view}
          </span>
        </div>

        <div className="cc-unit-detail-divider" />

        <div className="cc-unit-detail-body">
          {content ? (
            content.split("\n").map((line, index) => <p key={index}>{line}</p>)
          ) : (
            <p>등록된 내용이 없습니다.</p>
          )}
        </div>

        {files.length > 0 && (
          <div className="cc-unit-detail-attachments">
            {files.map((file, index) => (
              <div
                className="cc-unit-detail-attachment"
                key={file.id || `${file.name}-${index}`}
              >
                <span className="cc-unit-detail-attach-name">
                  {file.name}
                  {file.size ? ` (${file.size})` : ""}
                </span>

                {file.url ? (
                  <a
                    className="cc-unit-detail-attach-btn"
                    href={file.url}
                    rel="noopener noreferrer"
                    download={file.name || "첨부파일"}
                  >
                    ↓
                  </a>
                ) : (
                  <button className="cc-unit-detail-attach-btn" type="button">
                    ↓
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="cc-unit-detail-nav">
          <div
            className={`cc-unit-detail-nav-item ${!nextItem ? "disabled" : ""}`}
            onClick={() => nextItem && navigate(`/cc/${unit}/${nextItem.id}`)}
          >
            <span className="cc-unit-detail-nav-arrow">∧</span>

            <span className="cc-unit-detail-nav-text">
              {nextItem ? nextItem.title : "다음 글이 없습니다"}
            </span>
          </div>

          <div
            className={`cc-unit-detail-nav-item ${!prevItem ? "disabled" : ""}`}
            onClick={() => prevItem && navigate(`/cc/${unit}/${prevItem.id}`)}
          >
            <span className="cc-unit-detail-nav-arrow">∨</span>

            <span className="cc-unit-detail-nav-text">
              {prevItem ? prevItem.title : "이전 글이 없습니다"}
            </span>
          </div>
        </div>

        <div className="cc-unit-detail-back-row">
          <button
            className="cc-unit-detail-back-btn"
            onClick={() =>
              navigate(`/cc/${unit}`, {
                state: { activeTab: "template" },
              })
            }
            type="button"
          >
            목록
          </button>
        </div>
      </div>
    </div>
  );
}